import { fetchUserAttributes } from 'aws-amplify/auth';
import { authClient } from './amplifyClient';
import {
  createCompanyOwner as createCompanyOwnerMutation,
  createResourceLibrary as createResourceLibraryMutation,
} from '../graphql/mutations';
import { companyOwnersByCognitoId } from '../graphql/queries';
import { CompanyOwner } from '../types/schema';
import { CreateCompanyOwnerInput } from '../graphql/API';
import { DEFAULT_SUPER_ADMIN_PERMISSIONS, NETWORK_DOMAIN } from './constants';

export const FREE_DEMO_SIGNUP_FLAG = 'isFreeDemoSignup';

export const isFreeDemoSignupPending = (): boolean =>
  sessionStorage.getItem(FREE_DEMO_SIGNUP_FLAG) === 'true';

export const markFreeDemoSignup = (): void => {
  sessionStorage.setItem(FREE_DEMO_SIGNUP_FLAG, 'true');
};

export const clearFreeDemoSignup = (): void => {
  sessionStorage.removeItem(FREE_DEMO_SIGNUP_FLAG);
};

/**
 * Ensures a free-demo CompanyOwner exists for the signed-in Cognito user.
 * Returns the owner record (existing or newly created).
 */
export async function ensureFreeDemoCompanyOwner(
  fallbackCognitoId?: string,
): Promise<CompanyOwner> {
  const attributes = await fetchUserAttributes();
  const email = attributes.email as string | undefined;
  const name = (attributes.name as string | undefined) || email;
  const cognitoId = (attributes.sub as string | undefined) || fallbackCognitoId;

  if (!cognitoId || !email || !name) {
    throw new Error('Missing Cognito attributes for free-demo owner creation');
  }

  const existing = await authClient.graphql({
    query: companyOwnersByCognitoId,
    variables: { cognitoId },
  });
  const existingOwner = existing.data.companyOwnersByCognitoId.items[0];
  if (existingOwner) {
    return existingOwner as CompanyOwner;
  }

  const input: CreateCompanyOwnerInput = {
    cognitoId,
    email,
    name,
    subscriptionType: 'free-demo',
    role: 'super-admin',
    siteAdmin: false,
    discrepancyNotification: true,
    discrepancyNotificationTypes: `{ "inop": true, "warn": true }`,
    permissions: JSON.stringify(DEFAULT_SUPER_ADMIN_PERMISSIONS.map((item) => item.value)),
    domain: NETWORK_DOMAIN,
  };

  const response = await authClient.graphql({
    query: createCompanyOwnerMutation,
    variables: { input },
  });

  const created = response.data?.createCompanyOwner;
  if (!created?.id) {
    throw new Error('createCompanyOwner returned no id');
  }

  try {
    await authClient.graphql({
      query: createResourceLibraryMutation,
      variables: { input: { companyOwnerId: created.id } },
    });
  } catch (error) {
    // Resource library is best-effort; owner signup should still succeed.
    console.error('Error creating Resource Library:', error);
  }

  return created as CompanyOwner;
}
